import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import useRequest from '../../../components/hooks/use-req';
import { showToast } from '../../../components/toast';
import BackgroundWrapper from '../../../components/wrapper';
import { BVNIcon, GreenThick } from '../../../assets/icon';
import StepIndicator from '../../../components/indicator';
import Card from '../../../components/card';
import Button from '../../../components/button';
import { Footer } from '../../../components/layouts/footer';
import Input from '../../../components/input';
import { Bank, FormData } from '../../recommender/wallet/types';
import { ClockLoader } from 'react-spinners';
import SearchSelect from '../../../components/search-select';
import { MainHeader } from '../../../components/layouts/navbar';

const LinkYourBank: React.FC = () => {
    const navigate = useNavigate();
    const [banks, setBanks] = useState<Bank[]>([]);
    const authToken = localStorage.getItem("authToken");
    const [selectedBank, setSelectedBank] = useState<{ name: string; code: string } | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [selectedLinkedAccountId, setSelectedLinkedAccountId] = useState<string>('');
    const { handleSubmit, control, reset, setValue, watch } = useForm<FormData>();

    const { makeRequest: linkAccount, loading } = useRequest("/bank/bank/account", "POST", {
        Authorization: `Bearer ${authToken}`,
    });

    const { makeRequest: validateAccount, loading: validationLoading } = useRequest(
        "/bank/bank/validate-account",
        "POST",
        {
            Authorization: `Bearer ${authToken}`,
        }
    );

    const { makeRequest: getBanks } = useRequest(
        "/bank/bank/",
        "GET",
        {
            Authorization: `Bearer ${authToken}`,
        }
    );
    const accountNumber = watch('account_number');
    const bankCode = watch('bank_code');


    useEffect(() => {
        const fetchBanks = async () => {
            setLoadingBanks(true);

            const [response] = await getBanks();

            if (response?.status) {
                const sortedBanks = response?.data
                    .map((bank: any) => ({
                        id: bank.id,
                        code: bank.code,
                        name: bank.name,
                    }))
                    .sort((a: Bank, b: Bank) => a.name.localeCompare(b.name));

                setBanks(sortedBanks);
            } else {
                showToast(response?.message || "Failed to load banks", false);
            }

            setLoadingBanks(false);
        };

        fetchBanks();
    }, []);

    const handleBankSelect = (value: string) => {
        const selected = banks.find(bank => bank.name === value);
        if (selected) {
            setSelectedBank({
                name: selected.name,
                code: selected.code
            });
            setValue('bank_name', selected.name);
            setValue('bank_code', selected.code);
            setValidationError(''); // Clear validation error when bank changes
        }
    };

    useEffect(() => {
        const validateBankAccount = async () => {
            if (!bankCode || !accountNumber || accountNumber.length !== 10) {
                setValidationError('');
                return;
            }
            setValidationError('');
            const bankDetails = {
                bank_code: bankCode,
                account_number: accountNumber
            }
            const [validationResponse] = await validateAccount(bankDetails);

            if (validationResponse?.success) {
                // Auto-populate account name if validation is successful
                if (validationResponse?.account_name) {
                    setValue('account_name', validationResponse?.account_name);
                }
                setValidationError('');
            } else {
                setValidationError(validationResponse?.message || 'Invalid bank account details');
            }
        };

        // Add a small delay to avoid excessive API calls
        const timer = setTimeout(() => {
            validateBankAccount();
        }, 500);

        return () => clearTimeout(timer);
    }, [bankCode, accountNumber, setValue]);


    const handleLinkAccount = handleSubmit(async (formData: FormData) => {
        if (!selectedBank) {
            showToast('Please select a bank', false);
            return;
        }

        // Check if there's a validation error
        if (validationError) {
            showToast('Please fix the account validation error before submitting', false);
            return;
        }

        const linkBankPayload = {
            bank_name: selectedBank.name,
            bank_code: selectedBank.code,
            account_name: formData.account_name,
            account_number: formData.account_number,
        };

        const [linkResponse, error] = await linkAccount(linkBankPayload);

        if (linkResponse?.status) {
            showToast(linkResponse?.message, true);
            reset();
            setSelectedBank(null);
            setValidationError('');
            setSelectedLinkedAccountId('');
            navigate('/')

        } else {
            showToast(error?.message || 'Failed to link account', false);
        }
    });


    return (
        <BackgroundWrapper>
            <MainHeader />
            <div className="min-h-screen pt-32 md:pt-[146px] max-w-7xl mx-auto lg:px-[120px] px-[30px]">

                <div className="mb-6">
                    <h1 className="text-2xl md:text-[36px] font-bold text-gray-900 mb-2">
                        Welcome to VouchLinks 👋
                    </h1>
                    <p className="text-gray-600 text-[18px]">
                        Complete the final steps to unlock reward withdrawals and personalise your VouchLinks experience.
                    </p>
                    <StepIndicator currentStep={3} totalSteps={3} />
                </div>

                <div className="mx-auto grid md:grid-cols-2 gap-8">
                    {/* Interest Selection Card */}
                    <div className="flex justify-center items-center">
                        <Card>
                            <div className="p-6">
                                <h2 className="text-[22px] font-bold text-gray-900 mb-1">
                                    Link your bank account
                                </h2>
                                <p className="text-gray-500 font-medium text-[18px] mb-5">
                                    Link your bank account to securely receive reward payouts and withdraw your earnings.
                                </p>


                                {/* BVN */}

                                <form onSubmit={handleLinkAccount}>
                                    {/* Bank Selection */}
                                    <div className="mb-4">
                                        <Controller
                                            name="bank_name"
                                            control={control}
                                            rules={{ required: 'Bank name is required' }}
                                            render={({ field, fieldState }) => (
                                                <div>
                                                    <SearchSelect
                                                        {...field}
                                                        label="Bank Name"
                                                        options={banks.map((bank) => ({
                                                            value: bank.name,
                                                            label: bank.name,
                                                        }))}
                                                        className="w-full"
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            handleBankSelect(value);
                                                        }}
                                                        value={field.value}
                                                        disabled={loadingBanks}
                                                    />
                                                    {fieldState.error && (
                                                        <p className="mt-1 text-sm text-red-600">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                        {/* Hidden field for bank_code */}
                                        <Controller
                                            name="bank_code"
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} />
                                            )}
                                        />
                                    </div>

                                    {/* Account Number */}
                                    <div className="mb-4">
                                        <Controller
                                            name="account_number"
                                            control={control}
                                            rules={{
                                                required: 'Account number is required',
                                                pattern: {
                                                    value: /^\d{10}$/,
                                                    message: 'Account number must be 10 digits'
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <div>
                                                    <Input
                                                        {...field}
                                                        type="text"
                                                        placeholder="10-digit account number"
                                                        label='Account Number'
                                                        className="w-full"
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '')?.slice(0, 10);
                                                            field.onChange(value);
                                                        }}
                                                    />
                                                    {fieldState.error && (
                                                        <p className="mt-1 text-sm text-red-600">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                    {/* Validation status indicator */}
                                                    {field.value && field.value.length === 10 && bankCode && (
                                                        <div className="mt-2">
                                                            {validationLoading ? (
                                                                <div className="flex items-center text-sm text-gray-500">
                                                                    <ClockLoader size={12} className="mr-2" />
                                                                    Validating account...
                                                                </div>
                                                            ) : validationError ? (
                                                                <p className="text-sm text-red-600 flex items-center">
                                                                    <span className="mr-1">⚠</span>
                                                                    {validationError}
                                                                </p>
                                                            ) : (
                                                                <p className="text-sm text-green-600 flex items-center">
                                                                    <span className="mr-1">✓</span>
                                                                    Account validated successfully
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    {/* Account Holder Name */}
                                    <div className="mb-6">
                                        <Controller
                                            name="account_name"
                                            control={control}
                                            rules={{
                                                required: 'Account name is required',
                                                pattern: {
                                                    value: /^[A-Za-z\s]+$/,
                                                    message: 'Name should contain only letters and spaces'
                                                }
                                            }}
                                            render={({ field, fieldState }) => (
                                                <div>
                                                    <Input
                                                        {...field}
                                                        type="text"
                                                        placeholder="Account holder name"
                                                        className="w-full"
                                                        label='Name on Bank Account'
                                                    />
                                                    {fieldState.error && (
                                                        <p className="mt-1 text-sm text-red-600">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex space-x-3">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            className="w-full mb-3"
                                            type="submit"
                                            disabled={loading || loadingBanks || !selectedBank || !!validationError || validationLoading}
                                        >
                                            {loading ? (
                                                <ClockLoader color="#ffffff" loading={loading} size={20} />
                                            ) : (
                                                <>Confirm Bank Account</>
                                            )}
                                        </Button>
                                    </div>
                                </form>




                                {/* Skip Link */}
                                <Link
                                    to="/"
                                    className="text-center my-4 flex gap-1 justify-center font-medium text-sm mb-8 text-gray-500"
                                >
                                    Do this later?
                                    <span className="underline font-bold">Skip Step</span>
                                </Link>



                                <div className='flex flex-col bg-[#96A1AC1F] px-5 pt-3 rounded-lg  text-[14px]'>
                                    <span className="text-[#121212] h-[50px] gap-2  flex   mt-2">
                                        <svg width="24" height="24" className='w-[24px] h-[24px]' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clip-path="url(#clip0_6356_5560)">
                                                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#F37A50" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_6356_5560">
                                                    <rect width="24" height="24" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>

                                        Reward withdrawals will remain locked until BVN verification is completed.
                                    </span>
                                </div>

                            </div>
                        </Card>
                    </div>

                    {/* Hero Image */}
                    <div className="hidden w-full md:block">
                        <img src="/signupCover.png" alt="cover" />
                    </div>
                </div>
            </div>
            <Footer />
        </BackgroundWrapper>
    );
};

export default LinkYourBank;